'use client';

import React from 'react';
import { FieldError } from 'react-aria-components/FieldError';
import type { HelperTextProps } from '../../components/HelperText/HelperText.tsx';
import HelperText from '../../components/HelperText/HelperText.tsx';
import { cn } from '../../utils/cn';
export interface InputIndication {
    type: HelperTextProps['variant'];
    message: string;
}

export interface InputWithHelpersProps {
    hint?: React.ReactNode | string;
    indications?: InputIndication[];
}
export interface InputHelpersProps extends InputWithHelpersProps {
    wrapErrorInFieldError?: boolean;
    className?: string;
}

export function InputHelpers({ hint, indications, wrapErrorInFieldError = false, className }: InputHelpersProps) {
    const hasIndications = !!indications?.length;

    if (!hint && !hasIndications) return null;

    if (hint && !hasIndications) {
        return (
            <div className={className}>
                <HelperText
                    variant="subtle"
                    className={cn(`
                        text-lr-body-small-light
                        lr-md:text-lr-body-balanced-light
                    `)}
                >
                    {hint}
                </HelperText>
            </div>
        );
    }

    return (
        <div className={cn(indications!.length > 1 && 'grid grid-cols-2 gap-y-lr-spacing-100', className)}>
            {indications!.map((indication) => {
                const hideIcon =
                    indications!.length === 1 && (indication.type === 'success' || indication.type === 'error');

                const content = (
                    <HelperText
                        variant={indication.type}
                        hideIcon={hideIcon}
                        className={cn(`
                            text-lr-body-small-light
                            lr-md:text-lr-body-balanced-light
                        `)}
                    >
                        {indication.message}
                    </HelperText>
                );

                if (indication.type === 'error' && wrapErrorInFieldError) {
                    return <FieldError key={`error-${indication.message}`}>{content}</FieldError>;
                }

                return <React.Fragment key={`${indication.type}-${indication.message}`}>{content}</React.Fragment>;
            })}
        </div>
    );
}
